<template>
  <div class="pre-projects-filters">
    <!-- Budget bands ↔ the stored cost window ({ minCost, maxCost }); a null bound is open-ended. -->
    <FilterSingleChip
      is-text
      icon="dollar-sign"
      :label="$t('page.pre-projects.filters.budget')"
      :items="budgetItems"
      :value="selectedBudgetBand"
      @change="onBudgetChange"
    />

    <!-- Independent year picks: the exact selection is stored (and shown as
         checked); the backend's overlap window ({ fromYear, toYear }) is
         derived from its min…max span. -->
    <FilterMultiChip
      is-text
      icon="calendar"
      :label="$t('page.pre-projects.filters.year')"
      :items="yearItems"
      :value="selectedYears"
      @change="onYearsChange"
    />

    <!-- Emitters (SQI, municipalities, …): options come from the search
         response's availableEmitters (whole result set); the list is long,
         so the popover is searchable. -->
    <FilterMultiChip
      is-text
      searchable
      icon="briefcase"
      :label="$t('page.pre-projects.filters.emitter')"
      :items="emitterItems"
      :value="filters.emitters || []"
      @change="onEmittersChange"
    />

    <!-- Locations: an icon-trigger combobox — its own dropdown, no nested
         popover — over a province -> région -> ville tree. A fully selected
         province is sent as provinceIds, a fully selected region as
         regionIds, and partial city picks as cityIds. On the backend the
         three levels form ONE filter — a pre-project matches when any of
         its locations is a listed city, or lies in a listed region or province. -->
    <!-- Phones: a filter line like the others, the tree in its sheet. -->
    <FilterChip
      v-if="isPhone"
      is-text
      icon="map-pin"
      has-popup
      :label="locationsLabel"
      :pop-title="$t('page.pre-projects.filters.locations')"
      :active="locationsCount > 0"
      :open="locationsOpen"
      :show-clear="locationsCount > 0"
      @toggle="locationsOpen = !locationsOpen"
      @close="locationsOpen = false"
      @clear="onLocationsClear"
    >
      <BaseComboBox
        variant="list"
        searchable
        :value="selectedLocationValues"
        :options="locationOptions"
        :search-placeholder="$t('page.pre-projects.filters.locations-search')"
        :empty-text="$t('page.pre-projects.filters.no-results')"
        @input="onLocationsChange"
      />
    </FilterChip>
    <BaseComboBox
      v-else
      variant="icon"
      icon="map-pin"
      size="sm"
      searchable
      clearable
      :value="selectedLocationValues"
      :options="locationOptions"
      :placeholder="$t('page.pre-projects.filters.locations-placeholder')"
      :search-placeholder="$t('page.pre-projects.filters.locations-search')"
      :empty-text="$t('page.pre-projects.filters.no-results')"
      :remove-label="$t('page.pre-projects.filters.remove')"
      :clear-label="$t('filters.clear')"
      @input="onLocationsChange"
    />

    <FilterSingleChip
      icon="layers"
      :label="$t('page.pre-projects.filters.source')"
      :items="sourceItems"
      :value="selectedSource"
      @change="onSourceChange"
    />

    <!-- Phones clear them from the filter bar, and add a project from the page header. -->
    <BaseButton
      v-if="hasActiveFilters && !isPhone"
      class="pre-projects-filters__clearall"
      isSmall
      isTransparent
      isSquare
      hasIconLeft
      @click.native="onClearAll"
    >
      <BaseIcon name="x" />
      <span>{{ $t('filters.clear-all') }}</span>
    </BaseButton>

    <!-- Add a custom pre-project (the page owns the dialog). -->
    <BaseButton
      v-if="!isPhone"
      class="pre-projects-filters__add"
      isSquare
      hasIconLeft
      @click.native="$emit('add-project')"
    >
      <BaseIcon name="plus" />
      <span>{{ $t('page.pre-projects.form.add-project') }}</span>
    </BaseButton>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseComboBox from '@/components/base/BaseComboBox.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import FilterChip from '@/components/filters/FilterChip';
import FilterMultiChip from '@/components/filters/FilterMultiChip';
import FilterSingleChip from '@/components/filters/FilterSingleChip';
import { viewport } from '@/util/viewport';

import { mapActions, mapGetters } from 'vuex';

// Budget bands ↔ the backend cost window (like ScoreChip adapts bands ↔ ranges).
const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BUDGET_BANDS = [
  { value: 'under-100k', minCost: null, maxCost: 100 * THOUSAND },
  { value: '100k-500k', minCost: 100 * THOUSAND, maxCost: 500 * THOUSAND },
  { value: '500k-1m', minCost: 500 * THOUSAND, maxCost: 1 * MILLION },
  { value: '1m-2m', minCost: 1 * MILLION, maxCost: 2 * MILLION },
  { value: '2m-5m', minCost: 2 * MILLION, maxCost: 5 * MILLION },
  { value: 'over-5m', minCost: 5 * MILLION, maxCost: null },
];

// The year chip offers the current year plus the next five.
const YEAR_SPAN = 5;

/**
 * Pre-projects filter bar — composed from the shared filter chip kit, like TendersFilters.
 * Budget and Year are single-select chips whose options adapt to the backend's window filters.
 */
export default {
  name: 'PreProjectsFilters',
  components: {
    BaseButton,
    BaseComboBox,
    BaseIcon,
    FilterChip,
    FilterMultiChip,
    FilterSingleChip,
  },
  data() {
    return { locationsOpen: false };
  },
  computed: {
    ...mapGetters('preProjects', [
      'filters',
      'availableYears',
      'availableEmitters',
      'provinces',
      'regions',
      'allCities',
    ]),
    // The emitter options: the whole result set's emitters, keeping any
    // selected value in the list even when the results no longer cover it.
    emitterItems() {
      const emitters = new Set([...this.availableEmitters, ...(this.filters.emitters || [])]);
      return [...emitters].sort((a, b) => a.localeCompare(b)).map((emitter) => ({ value: emitter, text: emitter }));
    },
    // The locations combobox tree: province -> région -> ville. Regions with
    // no city in the lookup are dropped (nothing selectable), then provinces
    // with no region left; a region whose province is unknown to the lookup
    // lists at the top level so it stays reachable.
    locationOptions() {
      const regionOptions = this.regions
        .map((region) => ({
          value: region.id,
          label: region.name,
          provinceId: region.provinceId,
          children: this.allCities
            .filter((city) => city.regionId === region.id)
            .map((city) => ({ value: city.id, label: city.name })),
        }))
        .filter((option) => option.children.length);
      const provinceOptions = this.provinces
        .map((province) => ({
          value: province.id,
          label: province.name,
          children: regionOptions
            .filter((region) => region.provinceId === province.id)
            .map(({ value, label, children }) => ({ value, label, children })),
        }))
        .filter((option) => option.children.length);
      const provinceIds = new Set(this.provinces.map((province) => province.id));
      const orphanRegions = regionOptions
        .filter((region) => !provinceIds.has(region.provinceId))
        .map(({ value, label, children }) => ({ value, label, children }));
      return [...provinceOptions, ...orphanRegions];
    },
    // The combobox model is leaf (city) level: stored provinceIds and regionIds
    // expand to every city under them, plus the individually stored cityIds.
    selectedLocationValues() {
      const provinceIds = this.filters.provinceIds || [];
      const regionIds = new Set(this.filters.regionIds || []);
      this.regions.forEach((region) => {
        if (provinceIds.includes(region.provinceId)) regionIds.add(region.id);
      });
      const fromRegions = this.allCities.filter((city) => regionIds.has(city.regionId)).map((city) => city.id);
      return [...new Set([...fromRegions, ...(this.filters.cityIds || [])])];
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    // Provinces, regions and cities picked, each counted once (as the combobox shows them).
    locationsCount() {
      const { provinceIds, regionIds, cityIds } = this.filters;
      return [provinceIds, regionIds, cityIds].reduce((count, ids) => count + (ids || []).length, 0);
    },
    locationsLabel() {
      const label = this.$t('page.pre-projects.filters.locations');
      return this.locationsCount ? `${label} (${this.locationsCount})` : label;
    },
    sourceItems() {
      return [
        { value: 'aloha', text: this.$t('page.pre-projects.source.aloha') },
        { value: 'manual', text: this.$t('page.pre-projects.source.manual') },
      ];
    },
    selectedSource() {
      if (this.filters.manual === true) return 'manual';
      if (this.filters.manual === false) return 'aloha';
      return null;
    },
    hasActiveFilters() {
      const { minCost, maxCost, fromYear, toYear, years, query, emitters, provinceIds, regionIds, cityIds, manual } =
        this.filters;
      return (
        minCost != null ||
        maxCost != null ||
        fromYear != null ||
        toYear != null ||
        manual != null ||
        (years || []).length > 0 ||
        (query || []).length > 0 ||
        (emitters || []).length > 0 ||
        (provinceIds || []).length > 0 ||
        (regionIds || []).length > 0 ||
        (cityIds || []).length > 0
      );
    },
    budgetItems() {
      return BUDGET_BANDS.map((band) => ({
        value: band.value,
        text: this.$t(`page.pre-projects.filters.budget-bands.${band.value}`),
      }));
    },
    // The band matching the stored cost window, or null (e.g. a window set elsewhere).
    selectedBudgetBand() {
      const band = BUDGET_BANDS.find(
        (band) => band.minCost === this.filters.minCost && band.maxCost === this.filters.maxCost,
      );
      return band ? band.value : null;
    },
    yearItems() {
      // The search response's availableYears (whole result set), falling back
      // to the default window before the first response lands. The window's
      // bounds are always kept in the list so the selection stays visible
      // (and clearable) even when the filtered result set no longer covers it.
      const currentYear = new Date().getFullYear();
      const fallback = Array.from({ length: YEAR_SPAN + 1 }, (_, offset) => currentYear + offset);
      const years = new Set(this.availableYears.length ? this.availableYears : fallback);
      const { fromYear, toYear } = this.filters;
      if (fromYear != null) years.add(fromYear);
      if (toYear != null) years.add(toYear);
      return [...years].sort((a, b) => a - b).map((year) => ({ value: year, text: String(year) }));
    },
    // The checked years: the discrete picks when present; older saved filters
    // carrying only the window expand to its covered range.
    selectedYears() {
      const years = this.filters.years || [];
      if (years.length) return years;
      const { fromYear, toYear } = this.filters;
      if (fromYear == null && toYear == null) return [];
      const from = fromYear != null ? fromYear : toYear;
      const to = toYear != null ? toYear : fromYear;
      return this.yearItems.map((item) => item.value).filter((year) => year >= from && year <= to);
    },
  },
  methods: {
    ...mapActions('preProjects', ['setFilters', 'clearFilters', 'getProvinces', 'getRegions', 'getAllCities']),
    onBudgetChange(bandValue) {
      const band = BUDGET_BANDS.find((band) => band.value === bandValue) || null;
      this.setFilters({ minCost: band ? band.minCost : null, maxCost: band ? band.maxCost : null });
      this.$emit('change');
    },
    // Store the exact years, and derive the backend's overlap window (min…max of the selection) alongside.
    onYearsChange(years) {
      const numeric = (years || []).map(Number).filter((year) => !isNaN(year));
      this.setFilters({
        years: numeric,
        fromYear: numeric.length ? Math.min(...numeric) : null,
        toYear: numeric.length ? Math.max(...numeric) : null,
      });
      this.$emit('change');
    },
    onEmittersChange(emitters) {
      this.setFilters({ emitters: emitters || [] });
      this.$emit('change');
    },
    // Combobox model (flat city values) → the backend's split filters, compacted
    // from the top: a fully-selected province becomes its provinceId, else a
    // fully-selected region its regionId, else the picked cities stay cityIds.
    onLocationsChange(values) {
      const selected = new Set(values || []);
      const provinceIds = [];
      const regionIds = [];
      const cityIds = [];
      const leavesOf = (node) => (node.children ? node.children.flatMap(leavesOf) : [node.value]);
      const allPicked = (node) => leavesOf(node).every((value) => selected.has(value));
      const pickRegion = (region) => {
        const chosen = region.children.filter((city) => selected.has(city.value));
        if (!chosen.length) return;
        if (chosen.length === region.children.length) regionIds.push(region.value);
        else chosen.forEach((city) => cityIds.push(city.value));
      };
      this.locationOptions.forEach((node) => {
        const isProvince = node.children.some((child) => child.children);
        if (!isProvince) return pickRegion(node);
        if (allPicked(node)) return provinceIds.push(node.value);
        return node.children.forEach(pickRegion);
      });
      this.setFilters({ provinceIds, regionIds, cityIds });
      this.$emit('change');
    },
    onLocationsClear() {
      this.locationsOpen = false;
      this.onLocationsChange([]);
    },
    onSourceChange(source) {
      const manual = source === 'manual' ? true : source === 'aloha' ? false : null;
      this.setFilters({ manual });
      this.$emit('change');
    },
    onClearAll() {
      this.clearFilters();
      this.$emit('change');
    },
  },
  mounted() {
    // Location lookups for the locations combobox (all cached in the store).
    this.getProvinces().catch(() => {});
    this.getRegions().catch(() => {});
    this.getAllCities().catch(() => {});
  },
};
</script>

<style lang="scss">
// Mirrors .tenders-filters (chip row layout + softened clear-all).
.pre-projects-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  &__clearall.base-button .base-button__element {
    color: $color-slate-500;
    font-weight: bold;
  }

  // "Ajouter un projet" sits at the right end of the filter chips row.
  // TODO: manual alignment of button, to be replaced when doing BaseButton standardization
  &__add {
    display: flex;
    height: 100%;
    margin-left: auto;
    align-self: baseline;

    .base-button__element {
      min-height: 32px;
    }
  }
}
</style>
